import { ContentBlock, EditorState } from 'draft-js';
import followupStrategy from './followupStrategy';
import forgetFollowup from './modifiers/forgetFollowup';
import detectFollowupEntity from './utils/detectFollowupEntity';
import * as markdownUtils from './utils/markdown';
import forgetParsableFollowups from './modifiers/forgetParsableFollowups';
import followupComponent from './Followup';
import { EditorFns, FollowupPluginStore } from './types';
import { addFollowupIfCandidateFound, updateFollowupIfParsable } from './storeModifiers';
import Occasion from '@support/Occasion';
import { Dispatch, MutableRefObject } from 'react';
import entityAtChar from './utils/entityAtChar';
import { DefaultReminderTime } from '@reducers/user/metadata';

export const mdUtils = markdownUtils;

// When we create an EditorState from markdown for the first time (when making an editor editable),
// we should mark any parsable dates as skipParsingEntities -- if they aren't followup entities
// when the EditorState is initialized, we assume the user doesn't want them to be.
export const initializeSkipFollowupParsingEntities = (editorState: EditorState): EditorState => {
  // const [_, followupEntityState] = detectFollowupEntity(editorState)

  // By passing followupEntityState.exists, we can do an optimization here where we skip checking
  // whether or not parsed potential followups are already marked as entities... if no followup entity
  // already exists, we can just assume that we should forget *all* parsable followups. However, in the
  // event that there are no followup entities in the editorState but some other entity (namely a mention
  // entity) includes a parsable followup, we don't want to accidentally remove that other entity. We
  // therefore opt to skip this optimization by passing "true." The motivator of this change is that
  // we'd been dropping mentions for people with the name "Sun."
  return forgetParsableFollowups(editorState, true);
};

const checkAndUpdateFocusState = (
  editorState: EditorState,
  focusedBlock: ContentBlock,
  followupEntityHasFocus: boolean,
  setFollowupEntityHasFocus: (hasFocus: boolean) => void,
) => {
  const currentSelection = editorState.getSelection();
  const focusOffset = currentSelection.getFocusOffset();
  const contentState = editorState.getCurrentContent();
  const charList = focusedBlock.getCharacterList();
  const charAtFocus = charList.get(focusOffset);
  const entity = charAtFocus && entityAtChar(charAtFocus, contentState);

  if (entity?.isFollowupEntity && !followupEntityHasFocus) {
    setFollowupEntityHasFocus(true);
  } else if (!entity?.isFollowupEntity && followupEntityHasFocus) {
    setFollowupEntityHasFocus(false);
  }
};

export default function createFollowupPlugin(
  followupOccasion?: Occasion | null,
  setFollowupOccasion?: Dispatch<Occasion | null>,
  setFollowupEntityInteracted?: Dispatch<boolean>,
  delayParsingRef?: MutableRefObject<boolean>,
  readOnly?: boolean,
  defaultReminderTime?: DefaultReminderTime,
) {
  let initialized = false;
  let editorFns: EditorFns = {};
  let followupData = null;
  let _followupOccasion = followupOccasion;
  let followupEntityHovered = false;
  let followupEntityHasFocus = false;

  const _setFollowupOccasion = (occasion: Occasion | null) => {
    _followupOccasion = occasion;
    setFollowupOccasion?.(occasion);
  };

  // Followup entity interaction can happen two ways: the entity is hovered or has focus by way of
  // editor cursor position. We manage these two interactions separately -- hovering is a component state
  // level interaction, focus is an editor state level interaction.
  const setFollowupEntityHovered = (isHovered: boolean) => {
    followupEntityHovered = isHovered;
    if (setFollowupEntityInteracted) {
      setFollowupEntityInteracted(followupEntityHovered || followupEntityHasFocus);
    }
  };

  const setFollowupEntityHasFocus = (hasFocus: boolean) => {
    followupEntityHasFocus = hasFocus;
    if (setFollowupEntityInteracted) {
      setFollowupEntityInteracted(followupEntityHovered || followupEntityHasFocus);
    }
  };

  const setFollowupData = (followup: { text: string } | null) => (followupData = followup);

  const store: FollowupPluginStore = {
    initialized: () => initialized,
    setInitialized: () => (initialized = true),
    editorFns: () => editorFns,
    setEditorFns: (fns: EditorFns) => (editorFns = fns),
    followupData: () => followupData,
    followupOccasion: () => _followupOccasion,
    setFollowup: (followup: { occasion: Occasion; text: string } | null) => {
      if (!followup) {
        _setFollowupOccasion(null);
        setFollowupData(null);
        setFollowupEntityHovered(false);
        setFollowupEntityHasFocus(false);
      } else {
        _setFollowupOccasion(followup.occasion);
        setFollowupData({ text: followup.text });
      }
    },
    followupEntityHasFocus: () => followupEntityHasFocus,
    defaultReminderTime: () => defaultReminderTime,
  };

  return {
    onChange: (editorState: EditorState): EditorState => {
      const [focusedBlock, followupEntityState] = detectFollowupEntity(editorState);
      const storedFollowup = store.followupData();

      if (delayParsingRef?.current) {
        return editorState;
      }

      checkAndUpdateFocusState(
        editorState,
        focusedBlock,
        store.followupEntityHasFocus(),
        setFollowupEntityHasFocus,
      );

      // Case where we've deleted the entity in the last editor state change but
      // we still have it registered
      if (!followupEntityState.exists && storedFollowup) {
        store.setFollowup(null);
        return editorState;
      } else if (!followupEntityState.exists) {
        return addFollowupIfCandidateFound(editorState, store);
      } else if (storedFollowup && focusedBlock.getKey() == followupEntityState.blockKey) {
        return updateFollowupIfParsable(editorState, store, focusedBlock, followupEntityState);
      }

      return editorState;
    },

    initialize: ({ getEditorState, setEditorState }) => {
      const [_, followupEntityState] = detectFollowupEntity(getEditorState());
      store.setEditorFns({ getEditorState, setEditorState });
      store.setInitialized();

      if (followupEntityState.exists) {
        setFollowupData(followupEntityState.data);
      }
    },

    keyBindingFn: (e, { getEditorState, setEditorState }) => {
      const storedFollowup = store.followupData();

      if (e.key == 'Backspace' && storedFollowup) {
        const editorState = getEditorState();
        const selectionState = editorState.getSelection();
        const focusOffset = selectionState.getFocusOffset();
        const [_, followupEntityState] = detectFollowupEntity(editorState);

        if (selectionState.getFocusKey() != followupEntityState.blockKey) {
          return undefined;
        }

        const followupEntityEndIdx =
          followupEntityState.exists &&
          followupEntityState.offset + followupEntityState.data.text.length;

        // if focus is at the end of the entity, replace entity with SkipFollowupParsing entity
        if (focusOffset == followupEntityEndIdx) {
          store.setFollowup(null);
          setEditorState(forgetFollowup(editorState));

          return 'handled';
        }
      }

      return undefined;
    },

    decorators: [
      {
        strategy: followupStrategy,
        component: followupComponent(store, setFollowupEntityHovered, readOnly),
      },
    ],
  };
}
